import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { NumberFormatter } from '@repo/toolkit/number'
import { CustomOption } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import { TableRequiredFields } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'
import { isNumber } from 'lodash'

import { CenterStatus } from '../../../../lib/types/enums/center/center-status'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { teflTypeEnumMapping } from '../../../../lib/types/enums/ol/tefl-type.enum'
import { ProductTypeEnum } from '../../../../lib/types/enums/product/product-type.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { ArrivalAndDepartureProperties } from '../../../../lib/types/models/arrival-and-departure/arrival-and-departure.properties'
import {
  CenterArrivalDepartureTransfer,
  CenterArrivalDepartureTransferInput
} from '../../../../lib/types/models/center/center-arrival-departure-transfers'
import { CenterBudgets, JourneyBudget } from '../../../../lib/types/models/center/center-budget'
import {
  CenterCulturalOuting,
  CenterCulturalOutingInput
} from '../../../../lib/types/models/center/center-cultural-outing'
import {
  CenterExceptionalExpense,
  UpdateCenterExceptionalExpenseInput
} from '../../../../lib/types/models/center/center-exceptional-expenses'
import {
  CenterHostFamilyCost,
  CenterHostFamilyCostInput
} from '../../../../lib/types/models/center/center-host-family-cost'
import {
  CenterLocalOrganizer,
  CenterLocalOrganizerInput
} from '../../../../lib/types/models/center/center-local-organizer'
import {
  CenterTeachingHallCost,
  CenterTeachingHallCostInput
} from '../../../../lib/types/models/center/center-teaching-hall-costs'
import {
  CenterTeflCost,
  CenterTeflCostInput
} from '../../../../lib/types/models/center/center-tefl-cost'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { CenterActivity } from '../../../../lib/types/models/center-activity/center-activity'
import { CenterActivityInput } from '../../../../lib/types/models/center-activity/center-activity-input'
import { CountryData, getCountryLabel } from '../../../../lib/types/models/country/country-list'
import { CulturalOutingProperties } from '../../../../lib/types/models/cultural-outing/cultural-outing.properties'
import { IdInput } from '../../../../lib/types/utils'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { EditableTableColumn } from '../../../elements/editable-table'

export type CenterCostsTableMode = 'readonly' | 'free-edit' | 'action-edit'

export type CenterCostType = 'original-cost' | 'final-cost'

type TooltipType = 'ol' | 'init'

type FinalBalanceBadgeColor = { textColor?: string; color?: string }

// finalBalance = final total cost - payments already transferred: negative means the
// OL received more than the final cost (owed to LEC), positive means LEC still owes the OL.
export function getFinalBalanceBadgeColor(finalBalance?: number | null): FinalBalanceBadgeColor {
  if (!finalBalance) return {}
  if (finalBalance < 0)
    return { textColor: tailwindColors.white.DEFAULT, color: tailwindColors.green.DEFAULT }
  return { textColor: tailwindColors.white.DEFAULT, color: tailwindColors.red.DEFAULT }
}

type KeysWithTooltipType<T> = {
  tooltips: TooltipType[]
  editableColumns: T[]
}

type CenterCostsColumn<T extends TableRequiredFields> = EditableTableColumn<T> & {
  version: CenterCostType[]
}

export type CenterCostsTableProps = {
  currency: CurrencyEnum
  mode: CenterCostsTableMode
  costType: CenterCostType
}

type CenterCostTableTypes =
  | 'teaching-hall'
  | 'local-organizers'
  | 'host-family'
  | 'tefl'
  | 'cultural-outings'
  | 'miscellaneous'
  | 'arrival-departure-transfers'
  | 'activities'
  | 'budget'
  | 'transfers'
  | 'program-documents'

type CenterCostTablePermissions = Record<
  CenterCostTableTypes,
  Record<CenterStatus, { role: RoleEnum; mode: CenterCostsTableMode; costType: CenterCostType }[]>
>

/**
 * This is a record of the permissions for each role and status.
 * - ADMIN: can view and edit all costs at some point
 * - Ol: can view and edit all costs at some point
 * - PCR: can only view costs
 * - STAFF: can only view costs
 */
const centerCostPermissions: CenterCostTablePermissions = {
  'local-organizers': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  'host-family': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  tefl: {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  'teaching-hall': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' }
    ]
  },
  'cultural-outings': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  'arrival-departure-transfers': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  miscellaneous: {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  activities: {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  budget: {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  },
  transfers: {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'free-edit', costType: 'original-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'action-edit', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' }
    ]
  },
  'program-documents': {
    OPEN: [
      { role: RoleEnum.ADMIN, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    SENT_TO_LEC: [
      { role: RoleEnum.ADMIN, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ],
    VALIDATED: [
      { role: RoleEnum.ADMIN, mode: 'free-edit', costType: 'original-cost' },
      { role: RoleEnum.ADMIN, mode: 'readonly', costType: 'final-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'original-cost' },
      { role: RoleEnum.Ol, mode: 'readonly', costType: 'final-cost' }
    ]
  }
}

/**
 * This function checks if the user has the permission to edit the center costs based on the role and status
 * @param role - The role of the user
 * @param mode - The mode to check the permission for
 * @param status - The status of the center
 * @returns true if the user has the permission to edit the center costs, false otherwise
 */
export function getCenterCostsMode(props: {
  table: CenterCostTableTypes
  costType: CenterCostType
  role?: RoleEnum
  status?: CenterStatus
}): CenterCostsTableMode {
  const { role, status, table, costType } = props
  if (!role || !status) return 'readonly'

  const permissions = centerCostPermissions[table][status]
  const userPermission = permissions.find((p) => p.role === role && p.costType === costType)

  if (!userPermission) return 'readonly'
  return userPermission.mode
}

export function getCenterFinalCostsMode(props: {
  table: CenterCostTableTypes
  costType: CenterCostType
  role?: RoleEnum
  status?: CenterStatus
}): CenterCostsTableMode {
  const { role, status, table, costType } = props
  if (!role || !status) return 'readonly'

  const permissions = centerCostPermissions[table][status]
  const userPermission = permissions.find((p) => p.role === role && p.costType === costType)

  if (!userPermission) return 'readonly'
  return userPermission.mode
}

export const centerCostsTableModeMapping: ({
  role,
  status,
  costType
}: {
  role?: RoleEnum
  status?: CenterStatus
  costType: CenterCostType
}) => Record<CenterCostTableTypes, CenterCostsTableMode> = ({ role, status, costType }) => {
  return {
    'teaching-hall': getCenterCostsMode({ role, status, table: 'teaching-hall', costType }),
    'local-organizers': getCenterCostsMode({ role, status, table: 'local-organizers', costType }),
    'host-family': getCenterCostsMode({ role, status, table: 'host-family', costType }),
    tefl: getCenterCostsMode({ role, status, table: 'tefl', costType }),
    'cultural-outings': getCenterCostsMode({ role, status, table: 'cultural-outings', costType }),
    'arrival-departure-transfers': getCenterCostsMode({
      role,
      status,
      table: 'arrival-departure-transfers',
      costType
    }),
    miscellaneous: getCenterCostsMode({ role, status, table: 'miscellaneous', costType }),
    activities: getCenterCostsMode({ role, status, table: 'activities', costType }),
    budget: getCenterCostsMode({ role, status, table: 'budget', costType }),
    transfers: getCenterCostsMode({ role, status, table: 'transfers', costType }),
    'program-documents': getCenterCostsMode({ role, status, table: 'program-documents', costType })
  }
}

export function mapCenterTeachingHallCostToInput(
  data: CenterTeachingHallCost,
  costType: CenterCostType
): CenterTeachingHallCostInput {
  if (costType === 'final-cost') {
    return {
      id: data.id,
      costPerDay: data.finalCostPerDay,
      fixedCost: data.finalFixedCost,
      numberOfCourseDays: data.finalNumberOfCourseDays,
      numberOfHoursPerDay: data.finalNumberOfHoursPerDay,
      numberOfTeachingRoom: data.finalNumberOfTeachingRoom
    }
  }

  return {
    id: data.id,
    costPerDay: data.costPerDay,
    fixedCost: data.fixedCost,
    numberOfCourseDays: data.numberOfCourseDays,
    numberOfHoursPerDay: data.numberOfHoursPerDay,
    numberOfTeachingRoom: data.numberOfTeachingRoom
  }
}

export function mapCenterLocalOrganizerCostToInput(
  data: CenterLocalOrganizer,
  costType: CenterCostType
): CenterLocalOrganizerInput {
  return {
    id: data.id,
    fees: costType === 'final-cost' ? data.finalFees : data.fees,
    numberOfPeopleAssigned:
      costType === 'final-cost' ? data.finalNumberOfPeopleAssigned : data.numberOfPeopleAssigned
  }
}

export function mapCenterActivityCostToInput(
  data: CenterActivity,
  costType: CenterCostType
): CenterActivityInput {
  return {
    activityId: data.activityId,
    totalCost:
      costType === 'final-cost'
        ? data.finalTotalCostExcludingTransport
        : data.totalCostExcludingTransport,
    activityPaidBy: data.activityPaidBy,
    journeyIds: data.journeys.map((journey) => journey.id),
    startingTime: {
      hours: new Date(data.startingTime).getHours(),
      minutes: new Date(data.startingTime).getMinutes()
    },
    endingTime: {
      hours: new Date(data.endingTime).getHours(),
      minutes: new Date(data.endingTime).getMinutes()
    },
    date: data.date
  }
}

export function mapCenterHostFamilyCostToInput(
  data: CenterHostFamilyCost,
  costType: CenterCostType
): CenterHostFamilyCostInput {
  if (costType === 'final-cost') {
    return {
      id: data.id,
      costPerStay: data.finalCostPerStay,
      costPerNight: data.finalCostPerNight,
      costPerTuitionPerWeek: data.finalCostPerTuitionPerWeek,
      costPerSupplyVisitsPerWeek: data.finalCostPerSupplyVisitsPerWeek,
      numberOfPeopleAssigned: data.finalNumberOfPeopleAssigned
    }
  }

  return {
    id: data.id,
    costPerStay: data.costPerStay,
    costPerNight: data.costPerNight,
    costPerTuitionPerWeek: data.costPerTuitionPerWeek,
    costPerSupplyVisitsPerWeek: data.costPerSupplyVisitsPerWeek,
    numberOfPeopleAssigned: data.numberOfPeopleAssigned
  }
}

export function mapCenterExceptionalExpensesToInput(
  data: CenterExceptionalExpense,
  centerCostType: CenterCostType
): UpdateCenterExceptionalExpenseInput {
  return {
    id: data.id,
    amount: centerCostType === 'final-cost' ? data.finalAmount : data.amount,
    reason: data.reason
  }
}

export function mapCenterCulturalOutingsToInput(
  data: CenterCulturalOuting,
  costType: CenterCostType
): CenterCulturalOutingInput {
  if (costType === 'final-cost')
    return { id: data.id, culturalOutingId: data.outingId, cost: data.finalCost }
  return { id: data.id, culturalOutingId: data.outingId }
}

export function mapCenterTeflCostToInput(
  data: CenterTeflCost,
  costType: CenterCostType
): CenterTeflCostInput {
  if (costType === 'final-cost') {
    return {
      id: data.id,
      costPerDay: data.finalCostPerDay,
      costPerTefl: data.finalCostPerTefl,
      numberOfCourseDays: data.finalNumberOfCourseDays,
      numberOfTefl: data.finalNumberOfTefl
    }
  }

  return {
    id: data.id,
    costPerDay: data.costPerDay,
    costPerTefl: data.costPerTefl,
    numberOfCourseDays: data.numberOfCourseDays,
    numberOfTefl: data.numberOfTefl
  }
}

export function mapCenterArrivalDepartureTransfersToInput(
  data: CenterArrivalDepartureTransfer,
  costType: CenterCostType
): CenterArrivalDepartureTransferInput {
  if (costType === 'final-cost')
    return {
      id: data.id,
      arrivalAndDepartureId: data.arrivalAndDepartureId,
      foreignProviderArrivalPlaceId: data.foreignProviderArrivalPlaceId,
      cost: data.finalCost
    }

  return {
    id: data.id,
    arrivalAndDepartureId: data.arrivalAndDepartureId,
    foreignProviderArrivalPlaceId: data.foreignProviderArrivalPlaceId
  }
}

function getInitialValue(
  initialValue: number,
  finalValue: number,
  showCurrency: boolean = false
): number | string | undefined {
  if (initialValue === finalValue) return undefined
  if (showCurrency) return NumberFormatter.formatCurrency(initialValue)
  return initialValue
}

function getOLValue(olValue?: number, showCurrency: boolean = false): number | string | undefined {
  if (!olValue || isNaN(olValue)) return undefined
  if (showCurrency) return NumberFormatter.formatCurrency(olValue)
  return olValue
}

export const centerTeachingHallCostEditableColumns: Record<
  RoleEnum,
  KeysWithTooltipType<keyof CenterTeachingHallCost>
> = {
  [RoleEnum.ADMIN]: {
    editableColumns: [
      'numberOfTeachingRoom',
      'finalNumberOfTeachingRoom',
      'numberOfHoursPerDay',
      'finalNumberOfHoursPerDay',
      'numberOfCourseDays',
      'finalNumberOfCourseDays',
      'costPerDay',
      'finalCostPerDay',
      'fixedCost',
      'finalFixedCost'
    ],
    tooltips: ['init', 'ol']
  },
  [RoleEnum.Ol]: {
    editableColumns: ['costPerDay', 'finalCostPerDay', 'fixedCost', 'finalFixedCost'],
    tooltips: ['init']
  },
  [RoleEnum.STAFF]: { editableColumns: [], tooltips: [] },
  [RoleEnum.PCR]: { editableColumns: [], tooltips: [] },
  [RoleEnum.CLIENT]: { editableColumns: [], tooltips: [] },
  [RoleEnum.ACCOUNTANT]: { editableColumns: [], tooltips: [] },
  [RoleEnum.SYSTEM_ADMIN]: { editableColumns: [], tooltips: [] }
}

const centerTeachingHallCostsColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  editableColumns: (keyof CenterTeachingHallCost)[],
  tooltips: TooltipType[]
): CenterCostsColumn<CenterTeachingHallCost>[] => [
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.number.of.classrooms'),
    accessorKey: 'numberOfTeachingRoom',
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfTeachingRoom, data.numberOfTeachingRoom),
    isEditable: editableColumns.includes('numberOfTeachingRoom')
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.number.of.classrooms'),
    accessorKey: 'finalNumberOfTeachingRoom',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalNumberOfTeachingRoom'),
    initialValue: (data) =>
      getInitialValue(data.numberOfTeachingRoom, data.finalNumberOfTeachingRoom),
    cell: ({ row }) => row.original.finalNumberOfTeachingRoom
  },
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.hours.per.classroom'),
    accessorKey: 'numberOfHoursPerDay',
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfHoursPerDay, data.numberOfHoursPerDay),
    isEditable: editableColumns.includes('numberOfHoursPerDay')
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.hours.per.classroom'),
    accessorKey: 'finalNumberOfHoursPerDay',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalNumberOfHoursPerDay'),
    initialValue: (data) =>
      getInitialValue(data.numberOfHoursPerDay, data.finalNumberOfHoursPerDay),
    cell: ({ row }) => row.original.finalNumberOfHoursPerDay
  },
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.number.of.days'),
    accessorKey: 'numberOfCourseDays',
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfCourseDays, data.numberOfCourseDays),
    isEditable: editableColumns.includes('numberOfCourseDays')
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.number.of.days'),
    accessorKey: 'finalNumberOfCourseDays',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalNumberOfCourseDays'),
    initialValue: (data) => getInitialValue(data.numberOfCourseDays, data.finalNumberOfCourseDays),
    cell: ({ row }) => row.original.finalNumberOfCourseDays
  },
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.cost.per.day'),
    accessorKey: 'costPerDay',
    isEditable: editableColumns.includes('costPerDay'),
    centerCell: true,
    inputType: 'number',
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerDay, currency)}
        variant="primary"
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.cost.per.day'),
    accessorKey: 'finalCostPerDay',
    isEditable: editableColumns.includes('finalCostPerDay'),
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      tooltips.includes('init')
        ? getInitialValue(data.costPerDay, data.finalCostPerDay, true)
        : undefined,
    olValue: (data) => (tooltips.includes('ol') ? getOLValue(data.olCostPerDay, true) : undefined),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerDay, currency)}
        variant={
          (tooltips.includes('init') &&
            isNumber(getInitialValue(row.original.costPerDay, row.original.finalCostPerDay))) ||
          (tooltips.includes('ol') && isNumber(getOLValue(row.original.olCostPerDay)))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.fixed.cost'),
    accessorKey: 'fixedCost',
    isEditable: editableColumns.includes('fixedCost'),
    centerCell: true,
    inputType: 'number',
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.fixedCost, currency)}
        variant="primary"
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.fixed.cost'),
    accessorKey: 'finalFixedCost',
    isEditable: editableColumns.includes('finalFixedCost'),
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      tooltips.includes('init')
        ? getInitialValue(data.fixedCost, data.finalFixedCost, true)
        : undefined,
    olValue: (data) => (tooltips.includes('ol') ? getOLValue(data.olFixedCost, true) : undefined),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalFixedCost, currency)}
        variant={
          (tooltips.includes('init') &&
            isNumber(getInitialValue(row.original.fixedCost, row.original.finalFixedCost))) ||
          (tooltips.includes('ol') && isNumber(getOLValue(row.original.olFixedCost)))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.teaching.hall.table.header.total.cost'),
    accessorKey: 'totalCost',
    centerCell: true,
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.totalCost, currency)}
        variant={'primary'}
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.teaching.hall.table.header.total.cost'),
    accessorKey: 'finalTotalCost',
    centerCell: true,
    tooltip: true,
    initialValue: (data) =>
      tooltips.includes('init')
        ? getInitialValue(data.totalCost, data.finalTotalCost, true)
        : undefined,
    olValue: (data) => (tooltips.includes('ol') ? getOLValue(data.olTotalCost, true) : undefined),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalTotalCost, currency)}
        variant={
          (tooltips.includes('init') &&
            isNumber(getInitialValue(row.original.totalCost, row.original.finalTotalCost))) ||
          (tooltips.includes('ol') && isNumber(getOLValue(row.original.olTotalCost)))
            ? 'danger'
            : 'primary'
        }
      />
    )
  }
]

export const getCenterTeachingHallCostsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  editableColumns: (keyof CenterTeachingHallCost)[],
  tooltips: TooltipType[]
): EditableTableColumn<CenterTeachingHallCost>[] => {
  return centerTeachingHallCostsColumns(currency, t, editableColumns, tooltips).filter((column) =>
    column.version.includes(costType)
  )
}

const centerDetailedActivitiesColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  countryList?: CountryData[]
): CenterCostsColumn<IdInput<JourneyBudget>>[] => [
  {
    version: ['original-cost'],
    header: t('center.costs.detailed.activities.table.header.programmes'),
    accessorKey: 'journey.reference',
    centerCell: true,
    columns: [
      {
        header: ' ',
        accessorKey: 'journey.date.product.name',
        cell: ({ row }) => {
          const { journey } = row.original
          // The budget details modal is always displayed in English (see its locale keys)
          const countryName =
            getCountryLabel(
              'en',
              countryList?.find(({ key }) => key === journey.date.product.country.code)
            ) || journey.date.product.country.name
          const formula = getTranslatedName(journey.date.product.formula, 'en') || '—'
          const theme = getTranslatedName(journey.mainOption, 'en') || '—'
          const secondaryOptions = journey.secondaryOptions
            .map((secondaryOption) => getTranslatedName(secondaryOption.option, 'en') || '—')
            .join(' , ')

          return (
            <div>
              <Typography variant={'secondary-body'}>{journey.reference}</Typography>
              <Typography variant={'secondary-body'}>
                {`${countryName} - ${formula} - ${theme}${secondaryOptions ? ` - ${secondaryOptions}` : ''}`}
              </Typography>
            </div>
          )
        }
      }
    ]
  },
  // ------------------------------------
  {
    version: ['original-cost'],
    header: t('center.costs.detailed.activities.table.group.title.activity.cost'),
    accessorKey: 'totalCostExcludingTransport',
    cellClassName: 'border-l border-l-grey-light-hover',
    columnHeaderClassName: 'border-l border-grey-light-hover',
    centerCell: true,
    columns: [
      {
        header: t('center.costs.detailed.activities.table.header.number.of.students'),
        accessorKey: 'activityCost.numberOfStudents'
      },
      {
        header: t('center.costs.detailed.activities.table.header.average.cost.per.student'),
        accessorKey: 'activityCost.costPerStudent',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.activityCost.costPerStudent)}
          />
        )
      },
      {
        header: t('center.costs.detailed.activities.table.header.total.cost'),
        accessorKey: 'activityCost.totalCost',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.activityCost.totalCost)}
          />
        )
      }
    ]
  },
  // ------------------------------------
  {
    version: ['original-cost'],
    header: t('center.costs.detailed.activities.table.group.title.french.leader.admin.expenses'),
    accessorKey: 'totalCostExcludingTransport',
    centerCell: true,
    cellClassName: 'border-l border-l-grey-light-hover ',
    columnHeaderClassName: 'border-l border-l-grey-light-hover',
    columns: [
      {
        header: t('center.costs.detailed.activities.table.header.number.of.students'),
        accessorKey: 'adminExpenses.numberOfStudents'
      },
      {
        header: t('center.costs.detailed.activities.table.header.cost.per.student'),
        accessorKey: 'adminExpenses.costPerStudent',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.adminExpenses.costPerStudent)}
          />
        )
      },
      {
        header: t('center.costs.detailed.activities.table.header.total.cost'),
        accessorKey: 'adminExpenses.totalCost',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.adminExpenses.totalCost)}
          />
        )
      }
    ]
  },
  // ------------------------------------
  {
    version: ['original-cost'],
    header: t(
      'center.costs.detailed.activities.table.header.local.transport.ftr.and.french.leaders'
    ),
    accessorKey: '',
    centerCell: true,
    cellClassName: 'border-l border-l-grey-light-hover',
    columnHeaderClassName: 'border-l border-l-grey-light-hover',
    columns: [
      {
        header: t(
          'center.costs.detailed.activities.table.header.number.of.students.ftr.and.french.leaders'
        ),
        accessorKey: 'localTransport.numberOfPeople'
      },
      {
        header: t('center.costs.detailed.activities.table.header.total.cost'),
        accessorKey: 'localTransport.totalCost',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.localTransport.totalCost)}
          />
        )
      }
    ]
  },
  {
    version: ['original-cost'],
    header: t('center.costs.detailed.activities.table.header.total.cost'),
    cellClassName: 'border-l border-l-grey-light-hover',
    columnHeaderClassName: 'border-l border-l-grey-light-hover',
    accessorKey: 'totalCostExcludingTransport',
    centerCell: true,
    columns: [
      {
        header: '',
        accessorKey: 'totalCost',
        cell: ({ row }) => (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.totalCost)}
          />
        )
      }
    ]
  }
]

export const getCenterDetailedActivitiesColumns = (
  costType: CenterCostType,
  t: TFunction<AvailableLanguages, undefined>,
  countryList?: CountryData[]
): EditableTableColumn<IdInput<JourneyBudget>>[] => {
  return centerDetailedActivitiesColumns(t, countryList).filter((column) =>
    column.version.includes(costType)
  )
}

const centerLocalOrganizersColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): CenterCostsColumn<CenterLocalOrganizer>[] => [
  {
    version: ['original-cost', 'final-cost'],
    header: '',
    accessorKey: 'familyArrangement',
    isEditable: false,
    cell: ({ row }) => (
      <Typography variant={'secondary-body'}>
        {CenterProperties.getCenterOrganizerLabel(row.original, t)}
      </Typography>
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.local.organisers.fees.table.header.pax'),
    accessorKey: 'numberOfPeopleAssigned',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfPeopleAssigned, data.numberOfPeopleAssigned)
  },
  {
    version: ['final-cost'],
    header: t('center.costs.local.organisers.fees.table.header.pax'),
    accessorKey: 'finalNumberOfPeopleAssigned',
    isEditable: true,
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    initialValue: (data) =>
      getInitialValue(data.numberOfPeopleAssigned, data.finalNumberOfPeopleAssigned),
    cell: ({ row }) => row.original.finalNumberOfPeopleAssigned
  },
  {
    version: ['original-cost'],
    header: t('center.costs.local.organisers.fees.table.header.fees'),
    accessorKey: 'fees',
    isEditable: true,
    inputType: 'number',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalFees, data.fees, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.fees, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalFees, row.original.fees))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.local.organisers.fees.table.header.fees'),
    accessorKey: 'finalFees',
    isEditable: true,
    inputType: 'number',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.fees, data.finalFees, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalFees, currency)}
        variant={
          isNumber(getInitialValue(row.original.fees, row.original.finalFees))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.local.organisers.fees.table.header.total.cost'),
    accessorKey: 'totalFees',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalTotalFees, data.totalFees, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.totalFees, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalTotalFees, row.original.totalFees))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.local.organisers.fees.table.header.total.cost'),
    accessorKey: 'finalTotalFees',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.totalFees, data.finalTotalFees, true),
    cell: ({ row }) => {
      return (
        <Badge
          text={NumberFormatter.formatCurrency(row.original.finalTotalFees, currency)}
          variant={
            isNumber(getInitialValue(row.original.totalFees, row.original.finalTotalFees))
              ? 'danger'
              : 'primary'
          }
        />
      )
    }
  }
]

export const getCenterLocalOrganizersColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): EditableTableColumn<CenterLocalOrganizer>[] => {
  return centerLocalOrganizersColumns(currency, t).filter((column) =>
    column.version.includes(costType)
  )
}

type CenterHostFamilyCostsColumn = CenterCostsColumn<CenterHostFamilyCost> & {
  productType: ProductTypeEnum[]
}

// In a center mixing Club 4 and One to One stays, only the One to One rows have tuition and
// supply visits (the backend saves 0 on the others); in a One to One center every row has them
const isHostFamilyCostWithTuition = (
  data: CenterHostFamilyCost,
  productTypes: ProductTypeEnum[]
): boolean =>
  !productTypes.includes(ProductTypeEnum.CLUB_FOUR) ||
  data.productType === ProductTypeEnum.ONE_TO_ONE

const centerHostFamilyCostsColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  productTypes: ProductTypeEnum[]
): CenterHostFamilyCostsColumn[] => [
  {
    version: ['original-cost', 'final-cost'],
    header: '',
    accessorKey: 'familyArrangement',
    isEditable: false,
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    cell: ({ row }) => {
      return (
        <Typography variant={'secondary-body'}>
          {CenterProperties.getCenterOrganizerLabel(row.original, t, { showHours: true })}
        </Typography>
      )
    }
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.pax'),
    accessorKey: 'numberOfPeopleAssigned',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfPeopleAssigned, data.numberOfPeopleAssigned)
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.pax'),
    accessorKey: 'finalNumberOfPeopleAssigned',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.numberOfPeopleAssigned, data.finalNumberOfPeopleAssigned),
    cell: ({ row }) => row.original.finalNumberOfPeopleAssigned
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.cost.per.night'),
    accessorKey: 'costPerNight',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalCostPerNight, data.costPerNight, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerNight, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalCostPerNight, row.original.costPerNight))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.cost.per.night'),
    accessorKey: 'finalCostPerNight',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.costPerNight, data.finalCostPerNight, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerNight, currency)}
        variant={
          getInitialValue(row.original.costPerNight, row.original.finalCostPerNight)
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.cost.tuition.per.week'),
    accessorKey: 'costPerTuitionPerWeek',
    isEditable: true,
    isEditableCondition: (data) => isHostFamilyCostWithTuition(data, productTypes),
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.ONE_TO_ONE],
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalCostPerTuitionPerWeek, data.costPerTuitionPerWeek, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerTuitionPerWeek, currency)}
        variant={
          isNumber(
            getInitialValue(
              row.original.originalCostPerTuitionPerWeek,
              row.original.costPerTuitionPerWeek
            )
          )
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.cost.tuition.per.week'),
    accessorKey: 'finalCostPerTuitionPerWeek',
    isEditable: true,
    isEditableCondition: (data) => isHostFamilyCostWithTuition(data, productTypes),
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.ONE_TO_ONE],
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.costPerTuitionPerWeek, data.finalCostPerTuitionPerWeek, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerTuitionPerWeek, currency)}
        variant={
          getInitialValue(
            row.original.costPerTuitionPerWeek,
            row.original.finalCostPerTuitionPerWeek
          )
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.cost.supply.visits.per.week'),
    accessorKey: 'costPerSupplyVisitsPerWeek',
    isEditable: true,
    isEditableCondition: (data) => isHostFamilyCostWithTuition(data, productTypes),
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.ONE_TO_ONE],
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(
        data.originalCostPerSupplyVisitsPerWeek,
        data.costPerSupplyVisitsPerWeek,
        true
      ),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerSupplyVisitsPerWeek, currency)}
        variant={
          isNumber(
            getInitialValue(
              row.original.originalCostPerSupplyVisitsPerWeek,
              row.original.costPerSupplyVisitsPerWeek
            )
          )
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.cost.supply.visits.per.week'),
    accessorKey: 'finalCostPerSupplyVisitsPerWeek',
    isEditable: true,
    isEditableCondition: (data) => isHostFamilyCostWithTuition(data, productTypes),
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.ONE_TO_ONE],
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.costPerSupplyVisitsPerWeek, data.finalCostPerSupplyVisitsPerWeek, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(
          row.original.finalCostPerSupplyVisitsPerWeek,
          currency
        )}
        variant={
          getInitialValue(
            row.original.costPerSupplyVisitsPerWeek,
            row.original.finalCostPerSupplyVisitsPerWeek
          )
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.cost.per.host.family'),
    accessorKey: 'costPerStay',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalCostPerStay, data.costPerStay, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerStay, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalCostPerStay, row.original.costPerStay))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.cost.per.host.family'),
    accessorKey: 'finalCostPerStay',
    isEditable: true,
    centerCell: true,
    inputType: 'number',
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.costPerStay, data.finalCostPerStay, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerStay, currency)}
        variant={
          getInitialValue(row.original.costPerStay, row.original.finalCostPerStay)
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.host.family.table.header.total.cost'),
    accessorKey: 'totalCost',
    centerCell: true,
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalTotalCost, data.totalCost, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.totalCost, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalTotalCost, row.original.totalCost))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.host.family.table.header.total.cost'),
    accessorKey: 'finalTotalCost',
    centerCell: true,
    productType: [ProductTypeEnum.NORMAL, ProductTypeEnum.ONE_TO_ONE, ProductTypeEnum.CLUB_FOUR],
    tooltip: true,
    initialValue: (data) => getInitialValue(data.totalCost, data.finalTotalCost, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalTotalCost, currency)}
        variant={
          isNumber(getInitialValue(row.original.totalCost, row.original.finalTotalCost))
            ? 'danger'
            : 'primary'
        }
      />
    )
  }
]

export const getCenterHostFamilyCostsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  productTypes: ProductTypeEnum[]
): EditableTableColumn<CenterHostFamilyCost>[] => {
  return centerHostFamilyCostsColumns(currency, t, productTypes)
    .filter((column) => column.version.includes(costType))
    .filter(
      (column) =>
        productTypes.length === 0 ||
        productTypes.some((productType) => column.productType.includes(productType))
    )
}

export const centerArrivalDepartureTransfersEditableColumns: Record<
  RoleEnum,
  KeysWithTooltipType<keyof CenterArrivalDepartureTransfer>
> = {
  [RoleEnum.ADMIN]: {
    editableColumns: ['foreignProviderArrivalPlaceId'],
    tooltips: ['init', 'ol']
  },
  [RoleEnum.Ol]: { editableColumns: ['arrivalAndDepartureId'], tooltips: ['init'] },
  [RoleEnum.STAFF]: { editableColumns: ['arrivalAndDepartureId'], tooltips: [] },
  [RoleEnum.PCR]: { editableColumns: ['arrivalAndDepartureId'], tooltips: [] },
  [RoleEnum.CLIENT]: { editableColumns: ['arrivalAndDepartureId'], tooltips: [] },
  [RoleEnum.ACCOUNTANT]: { editableColumns: [], tooltips: [] },
  [RoleEnum.SYSTEM_ADMIN]: { editableColumns: [], tooltips: [] }
}

type CenterArrivalDepartureOptions = {
  arrivalAndDeparturesOptions: CustomOption<string | number>[]
  tripAddressOptions: CustomOption<string | number>[]
  editableColumns: (keyof CenterArrivalDepartureTransfer)[]
  tooltips: TooltipType[]
}

const centerArrivalDepartureTransfersColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  options: CenterArrivalDepartureOptions
): CenterCostsColumn<CenterArrivalDepartureTransfer>[] => {
  const { arrivalAndDeparturesOptions, tripAddressOptions, editableColumns, tooltips } = options
  return [
    {
      version: ['original-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.from.to'),
      accessorKey: 'foreignProviderArrivalPlaceId',
      inputType: 'select',
      options: tripAddressOptions,
      isEditable: editableColumns.includes('foreignProviderArrivalPlaceId'),
      cell: ({ row }) => valueRender(row.original.foreignProviderArrivalPlace, (f) => f.label)
    },
    {
      version: ['final-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.from.to'),
      accessorKey: 'foreignProviderArrivalPlaceId',
      cell: ({ row }) => valueRender(row.original.foreignProviderArrivalPlace, (f) => f.label)
    },
    {
      version: ['original-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.quotation'),
      accessorKey: 'arrivalAndDepartureId',
      inputType: 'select',
      options: arrivalAndDeparturesOptions,
      optional: true,
      isEditable: editableColumns.includes('arrivalAndDepartureId'),
      cell: ({ row }) =>
        valueRender(row.original.arrivalAndDeparture, (ad) =>
          ArrivalAndDepartureProperties.getLabel(ad)
        )
    },
    {
      version: ['final-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.quotation'),
      accessorKey: 'arrivalAndDepartureId',
      cell: ({ row }) =>
        valueRender(row.original.arrivalAndDeparture, (ad) =>
          ArrivalAndDepartureProperties.getLabel(ad)
        )
    },
    {
      version: ['original-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.total.cost'),
      accessorKey: 'cost',
      cell: ({ row }) =>
        row.original.cost > 0 ? (
          <Badge
            variant={'primary'}
            text={NumberFormatter.formatCurrency(row.original.cost, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      version: ['final-cost'],
      header: t('center.costs.arrival.departure.transfers.table.header.total.cost'),
      accessorKey: 'finalCost',
      isEditable: true,
      inputType: 'number',
      tooltip: true,
      initialValue: (data) =>
        tooltips.includes('init') ? getInitialValue(data.cost, data.finalCost, true) : undefined,
      olValue: (data) => (tooltips.includes('ol') ? getOLValue(data.olCost, true) : undefined),
      cell: ({ row }) =>
        row.original.finalCost > 0 ? (
          <Badge
            variant={
              (tooltips.includes('init') &&
                isNumber(getInitialValue(row.original.cost, row.original.finalCost))) ||
              (tooltips.includes('ol') && isNumber(getOLValue(row.original.olCost)))
                ? 'danger'
                : 'primary'
            }
            text={NumberFormatter.formatCurrency(row.original.finalCost, currency)}
          />
        ) : (
          '-'
        )
    }
  ]
}

export const getCenterArrivalDepartureTransfersColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  options: CenterArrivalDepartureOptions
): EditableTableColumn<CenterArrivalDepartureTransfer>[] => {
  return centerArrivalDepartureTransfersColumns(currency, t, options).filter((column) =>
    column.version.includes(costType)
  )
}

export const centerFrenchLeaderCostsEditableColumns: Record<RoleEnum, TooltipType[]> = {
  ADMIN: ['init', 'ol'],
  Ol: ['init'],
  STAFF: [],
  PCR: [],
  CLIENT: [],
  ACCOUNTANT: [],
  SYSTEM_ADMIN: []
}

const centerFrenchLeaderCostsColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): CenterCostsColumn<IdInput<CenterBudgets>>[] => [
  {
    version: ['original-cost', 'final-cost'],
    header: t(
      'center.costs.french.leader.budget.table.header.activity.budget.paid.by.french.leader'
    ),
    accessorKey: 'totalActivityCost',
    centerCell: true,
    cell: ({ row }) => (
      <Badge
        variant={'primary'}
        text={NumberFormatter.formatCurrency(row.original.totalActivityCost, currency)}
      />
    )
  },
  {
    version: ['original-cost', 'final-cost'],
    header: t('center.costs.french.leader.budget.table.header.total.activity.budget'),
    accessorKey: 'totalAdminExpenses',
    centerCell: true,
    cell: ({ row }) => (
      <Badge
        variant={'primary'}
        text={NumberFormatter.formatCurrency(row.original.totalAdminExpenses, currency)}
      />
    )
  },
  {
    version: ['original-cost', 'final-cost'],
    header: t('center.costs.french.leader.budget.table.header.french.leaders.local.transport'),
    accessorKey: 'totalLocalTransport',
    centerCell: true,
    cell: ({ row }) => (
      <Badge
        variant={'primary'}
        text={NumberFormatter.formatCurrency(row.original.totalLocalTransport, currency)}
      />
    )
  }
]

export const getCenterFrenchLeaderCostsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): EditableTableColumn<IdInput<CenterBudgets>>[] => {
  return centerFrenchLeaderCostsColumns(currency, t).filter((column) =>
    column.version.includes(costType)
  )
}

export const centerTeflCostsEditableColumns: Record<RoleEnum, (keyof CenterTeflCost)[]> = {
  [RoleEnum.ADMIN]: [
    'numberOfTefl',
    'finalNumberOfTefl',
    'numberOfCourseDays',
    'finalNumberOfCourseDays',
    'costPerDay',
    'finalCostPerDay',
    'costPerTefl',
    'finalCostPerTefl'
  ],
  [RoleEnum.Ol]: ['costPerDay', 'costPerTefl'],
  [RoleEnum.STAFF]: [],
  [RoleEnum.PCR]: [],
  [RoleEnum.CLIENT]: [],
  [RoleEnum.ACCOUNTANT]: [],
  [RoleEnum.SYSTEM_ADMIN]: []
}

const centerTeflCostsColumns: (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  editableColumns: (keyof CenterTeflCost)[]
) => CenterCostsColumn<CenterTeflCost>[] = (currency, t, editableColumns) => [
  {
    version: ['original-cost', 'final-cost'],
    header: '',
    accessorKey: 'type',
    cell: ({ row }) => (
      <Typography variant={'secondary-body'}>
        {t(teflTypeEnumMapping[row.original.type])}
      </Typography>
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.number.of.tefl'),
    accessorKey: 'numberOfTefl',
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalNumberOfTefl, data.numberOfTefl),
    isEditable: editableColumns.includes('numberOfTefl')
  },
  {
    version: ['final-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.number.of.tefl'),
    accessorKey: 'finalNumberOfTefl',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalNumberOfTefl'),
    initialValue: (data) => getInitialValue(data.numberOfTefl, data.finalNumberOfTefl),
    cell: ({ row }) => row.original.finalNumberOfTefl
  },
  {
    version: ['original-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.number.of.days'),
    accessorKey: 'numberOfCourseDays',
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      getInitialValue(data.originalNumberOfCourseDays, data.numberOfCourseDays),
    isEditable: editableColumns.includes('numberOfCourseDays')
  },
  {
    version: ['final-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.number.of.days'),
    accessorKey: 'finalNumberOfCourseDays',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalNumberOfCourseDays'),
    initialValue: (data) => getInitialValue(data.numberOfCourseDays, data.finalNumberOfCourseDays),
    cell: ({ row }) => row.original.finalNumberOfCourseDays
  },
  {
    version: ['original-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.per.day'),
    accessorKey: 'costPerDay',
    isEditable: editableColumns.includes('costPerDay'),
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalCostPerDay, data.costPerDay, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerDay, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalCostPerDay, row.original.costPerDay))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.per.day'),
    accessorKey: 'finalCostPerDay',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalCostPerDay'),
    initialValue: (data) => getInitialValue(data.costPerDay, data.finalCostPerDay, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerDay, currency)}
        variant={
          getInitialValue(row.original.costPerDay, row.original.finalCostPerDay)
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.per.tefl'),
    accessorKey: 'costPerTefl',
    isEditable: editableColumns.includes('costPerTefl'),
    centerCell: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalCostPerTefl, data.costPerTefl, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.costPerTefl, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalCostPerTefl, row.original.costPerTefl))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.per.tefl'),
    accessorKey: 'finalCostPerTefl',
    centerCell: true,
    tooltip: true,
    inputType: 'number',
    isEditable: editableColumns.includes('finalCostPerTefl'),
    initialValue: (data) => getInitialValue(data.costPerTefl, data.finalCostPerTefl, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalCostPerTefl, currency)}
        variant={
          getInitialValue(row.original.costPerTefl, row.original.finalCostPerTefl)
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['original-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.total'),
    accessorKey: 'totalCost',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.originalTotalCost, data.totalCost, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.totalCost, currency)}
        variant={
          isNumber(getInitialValue(row.original.originalTotalCost, row.original.totalCost))
            ? 'danger'
            : 'primary'
        }
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.tefl.teachers.and.leaders.table.header.cost.total'),
    accessorKey: 'finalTotalCost',
    centerCell: true,
    tooltip: true,
    initialValue: (data) => getInitialValue(data.totalCost, data.finalTotalCost, true),
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalTotalCost, currency)}
        variant={
          getInitialValue(row.original.totalCost, row.original.finalTotalCost)
            ? 'danger'
            : 'primary'
        }
      />
    )
  }
]

export const getCenterTeflCostsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  editableColumns: (keyof CenterTeflCost)[]
): EditableTableColumn<CenterTeflCost>[] => {
  return centerTeflCostsColumns(currency, t, editableColumns).filter((column) =>
    column.version.includes(costType)
  )
}

export const centerCulturalOutingsEditableColumns: Record<RoleEnum, TooltipType[]> = {
  ADMIN: ['init', 'ol'],
  Ol: ['init'],
  STAFF: [],
  PCR: [],
  CLIENT: [],
  ACCOUNTANT: [],
  SYSTEM_ADMIN: []
}

const centerCulturalOutingsColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  culturalOutingOptions: CustomOption<string | number>[],
  tooltips: TooltipType[]
): CenterCostsColumn<CenterCulturalOuting>[] => [
  {
    version: ['original-cost'],
    header: t('center.costs.cultural.outings.table.header.cultural.outings'),
    accessorKey: 'outingId',
    columnHeaderClassName: 'w-4/5',
    isEditable: true,
    inputType: 'select',
    options: culturalOutingOptions,
    optional: true,
    inputLabel: t('center.costs.cultural.outings.table.header.cultural.outings.label'),
    cell: ({ row }) =>
      valueRender(row.original.outing, (oc) => CulturalOutingProperties.getLabel(oc, t))
  },
  {
    version: ['final-cost'],
    header: t('center.costs.cultural.outings.table.header.cultural.outings'),
    accessorKey: 'outingId',
    columnHeaderClassName: 'w-4/5',
    cell: ({ row }) =>
      valueRender(row.original.outing, (oc) => CulturalOutingProperties.getLabel(oc, t))
  },
  {
    version: ['original-cost'],
    header: t('center.costs.cultural.outings.table.header.total.cost'),
    accessorKey: 'cost',
    centerCell: true,
    cell: ({ row }) =>
      row.original.cost > 0 ? (
        <Badge
          variant={'primary'}
          text={NumberFormatter.formatCurrency(row.original.cost, currency)}
        />
      ) : (
        '-'
      )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.cultural.outings.table.header.total.cost'),
    accessorKey: 'finalCost',
    centerCell: true,
    isEditable: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      tooltips.includes('init') ? getInitialValue(data.cost, data.finalCost, true) : undefined,
    olValue: (data) => (tooltips.includes('ol') ? getOLValue(data.olCost, true) : undefined),
    cell: ({ row }) =>
      isNumber(row.original.finalCost) ? (
        <Badge
          variant={
            (tooltips.includes('init') &&
              isNumber(getInitialValue(row.original.cost, row.original.finalCost))) ||
            (tooltips.includes('ol') && isNumber(getOLValue(row.original.olCost)))
              ? 'danger'
              : 'primary'
          }
          text={NumberFormatter.formatCurrency(row.original.finalCost, currency)}
        />
      ) : (
        '-'
      )
  }
]

export const getCenterCulturalOutingsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  culturalOutingOptions: CustomOption<string | number>[],
  tooltips: TooltipType[]
): EditableTableColumn<CenterCulturalOuting>[] => {
  return centerCulturalOutingsColumns(currency, t, culturalOutingOptions, tooltips).filter(
    (column) => column.version.includes(costType)
  )
}

export const centerActivitiesEditableColumns: Record<RoleEnum, TooltipType[]> = {
  ADMIN: ['init', 'ol'],
  Ol: ['init'],
  STAFF: [],
  PCR: [],
  CLIENT: [],
  ACCOUNTANT: [],
  SYSTEM_ADMIN: []
}

const centerActivitiesColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  tooltips: TooltipType[]
): CenterCostsColumn<CenterActivity>[] => [
  {
    version: ['original-cost', 'final-cost'],
    header: t('center.costs.activity.budget.table.header.activities'),
    accessorKey: 'activity.name',
    enableSorting: true
  },
  {
    version: ['original-cost'],
    header: t('center.costs.activity.budget.table.header.total.cost'),
    accessorKey: 'totalCostExcludingTransport',
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant={'primary'}
        text={NumberFormatter.formatCurrency(row.original.totalCostExcludingTransport, currency)}
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.activity.budget.table.header.total.cost'),
    accessorKey: 'finalTotalCostExcludingTransport',
    enableSorting: true,
    isEditable: true,
    inputType: 'number',
    tooltip: true,
    initialValue: (data) =>
      tooltips.includes('init')
        ? getInitialValue(
            data.totalCostExcludingTransport,
            data.finalTotalCostExcludingTransport,
            true
          )
        : undefined,
    cell: ({ row }) => (
      <Badge
        variant={
          isNumber(
            getInitialValue(
              row.original.totalCostExcludingTransport,
              row.original.finalTotalCostExcludingTransport
            )
          ) || isNumber(getOLValue(row.original.olTotalCostExcludingTransport))
            ? 'danger'
            : 'primary'
        }
        text={NumberFormatter.formatCurrency(
          row.original.finalTotalCostExcludingTransport,
          currency
        )}
      />
    )
  }
]

export const getCenterActivitiesColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>,
  tooltips: TooltipType[]
): EditableTableColumn<CenterActivity>[] => {
  return centerActivitiesColumns(currency, t, tooltips).filter((column) =>
    column.version.includes(costType)
  )
}

export const centerMiscellaneousCostsColumns = (
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): CenterCostsColumn<CenterExceptionalExpense>[] => [
  {
    version: ['original-cost', 'final-cost'],
    header: t('center.costs.miscellaneous.table.header.reason'),
    accessorKey: 'reason',
    isEditable: true,
    inputType: 'text',
    inputClassName: 'px-2 text-left',
    inputWrapperClassName: 'w-full',
    isEditableCondition: (data) => !data.validated,
    cell: ({ row }) => <Typography variant={'secondary-body'}>{row.original.reason}</Typography>
  },
  {
    version: ['original-cost'],
    header: t('center.costs.miscellaneous.table.header.amount'),
    accessorKey: 'amount',
    centerCell: true,
    isEditable: true,
    inputType: 'number',
    isEditableCondition: (data) => !data.validated,
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.amount ?? 0, currency)}
        variant={'primary'}
      />
    )
  },
  {
    version: ['final-cost'],
    header: t('center.costs.miscellaneous.table.header.amount'),
    accessorKey: 'finalAmount',
    centerCell: true,
    isEditable: true,
    inputType: 'number',
    isEditableCondition: (data) => !data.validated,
    cell: ({ row }) => (
      <Badge
        text={NumberFormatter.formatCurrency(row.original.finalAmount, currency)}
        variant={'primary'}
      />
    )
  }
]

export const getCenterMiscellaneousCostsColumns = (
  costType: CenterCostType,
  currency: CurrencyEnum,
  t: TFunction<AvailableLanguages, undefined>
): EditableTableColumn<CenterExceptionalExpense>[] => {
  return centerMiscellaneousCostsColumns(currency, t).filter((column) =>
    column.version.includes(costType)
  )
}
